import consoleLogger from '../../logger/consoleLogger';
import { PreloaderScreen } from './preloaderScreen';
import { AClient } from '../aClient';
import { Browsers } from '../../constants/Browsers';
import { GameClient, GameClientLoadingState } from '../gameClient';
import { SetKeyboardEnabledCommand } from '../clientCommands/setKeyboardEnabledCommand';
import { ClientEventEmitter } from '../../marketing/ClientEventEmitter';
import { ClientEvents } from '../../constants/clientEvents';
import { PasteClipboardContentCommand } from '../clientCommands/pasteClipboardContentCommand';
import { GtmSender } from '../../marketing/GtmSender';
import { WebglEvents } from '../../constants/gameEvents';
import { onReadyCalcBack } from '../../general/calcIndexBackAndPopup';
import { StatWebglLoadingHandler } from '../loadingHandler/statWebglLoadingHandler';
import { getPreloaderPercentsLayout, PRELOADER_PERCENTS_REAL } from '../loadingHandler/preloaderPercentsLayout';
import { WebglLoadingAssistant } from './WebglLoadingAssistant';
import { isGetUserFinished } from '../../general/modules/getUser';
import { DownloadScreen } from './downloadScreen';
import { NoWebglScreen } from './noWebglScreen';
import { Webgl2020GameInstance } from '../../types/client';
import { NewGameVideoPreloader } from './newGameVideoPreloader';
import { catchPaste } from './clipboardUtils';
import { Stat } from '../../stat/Stat';
import { UserStatEntryType } from '../../stat/UserStatEntryType';
import { EnableSoundCommand } from '../clientCommands/enableSoundCommand';
import { sendCreateUnityInstanceFailedFact, sendNewLoaderFact } from '../../stat/factHelpers';
import { NewLoaderFacts } from './new-loader-facts';
import { LoadingProgressEventEmitter } from '../loadingHandler/loadingProgressEventEmitter';
import { NormalWebglLoadingHandler } from '../loadingHandler/normalWebglLoadingHandler';
import { ErrorSmallPopup } from '../../popups/errorSmallPopup';
import { WebglNotSupportedPopup } from './webglNotSupportedPopup';
import { AdStateAuthFactory } from '../../marketing/ad-state-auth-factory';
import { NewgameWebglLoadingHandler } from '../loadingHandler/newgameWebglLoadingHandler';
import { Localization } from '../../localization/localization';
import { UserSession } from '../../general/modules/user/userSession';
import { getWebglSendbirdApi } from './webglClientDependencies';
import { SetTabState } from '../clientCommands/setTabState';
import { showMinimizedSupportChat } from '../../supportTicket/supportWindow';

const logger = consoleLogger.getPrefixedCopy('WEBGL_CLIENT');

const UNITY_CONTAINER_ID = 'gameContainer';
const UNITY_CONTAINER_SELECTOR = `#${UNITY_CONTAINER_ID}`;
const UNITY_CANVAS_SELECTOR = `${UNITY_CONTAINER_SELECTOR} #unityCanvas`;

export class WebglClient extends AClient {
    private static heightPercent: number = 100;
    private static widthPercent: number = 100;

    // eslint-disable-next-line max-lines-per-function
    public static async embed() {
        const preloaderScreen = PreloaderScreen.getInstance();
        const shouldShowNewgamePreloader = await this.getShouldShowNewgamePreloader();
        const client = GameClient.getInstance();

        isGetUserFinished()
            .then(()=>{
                StatWebglLoadingHandler.sendNewUserPrecompileStageFact('getUserInstall');
            })
            .finally(async() => {
                WebglClient.showPreloaderBg();
                const isNewGameProgress = await UserSession.getInstance().isNewProgress();
                if (isNewGameProgress) {
                    preloaderScreen.showFirstSessionWarning();
                }
            });

        if (shouldShowNewgamePreloader) {
            Stat.sendEvent(UserStatEntryType.client_preloader_video_initiating);
            await NewGameVideoPreloader.showVideoPreloader(() => {
                const isGameLoaded = client.getLoadingState() === GameClientLoadingState.LOADED;

                if (isGameLoaded) {
                    GameClient.show();
                    client.sendCommand(new EnableSoundCommand());
                } else {
                    LoadingProgressEventEmitter.addLoadingProgressHandler(new NewgameWebglLoadingHandler());
                    preloaderScreen.show();
                    preloaderScreen.showPreloader();
                }
            }).catch((error: Error) => {
                window.Sentry.captureException(error);
                console.error('Failed to show video preloader', error);
            });
        } else {
            preloaderScreen.show();
            preloaderScreen.showPreloader();
        }

        await this.provideClientDependencies();
        preloaderScreen.setLoadingProgress(getPreloaderPercentsLayout().initialPercent);
        StatWebglLoadingHandler.sendNewUserPrecompileStageFact('clientEmbedStart');
        WebglClient.setListeners();

        if (!WebglClient.getIsWebglSupported()) {
            preloaderScreen.hide();
            NoWebglScreen.show();
            return;
        }

        // TODO здесь этого не должно быть, тут не должен быть вызыва embed вообще
        if (!window.app.isDesktop) {
            DownloadScreen.show();
            return;
        }

        ClientEventEmitter.fireEvent(ClientEvents.WEBGL_START_LOADING);
        LoadingProgressEventEmitter.addLoadingProgressHandler(new NormalWebglLoadingHandler());
        const statWebglLoadingHandler = new StatWebglLoadingHandler();
        LoadingProgressEventEmitter.addLoadingProgressHandler(statWebglLoadingHandler);
        await WebglClient.clientLoaderReady();
        StatWebglLoadingHandler.sendNewUserPrecompileStageFact('unityLoaderReady');

        try {
            WebglLoadingAssistant.enable();
        } catch (error) {
            consoleLogger.error(error);
            window.Sentry.captureException(error);
        }

        statWebglLoadingHandler.startTimer();
        StatWebglLoadingHandler.sendNewUserPrecompileStageFact('instantiateStarting');
        LoadingProgressEventEmitter.firePreStartEvent(PRELOADER_PERCENTS_REAL.initialPercent);

        this.applyFbGamepadBugFix();
        window.createUnityInstance(
            $<HTMLCanvasElement>(UNITY_CANVAS_SELECTOR).get(0),
            this.getInstantiateConfig(),
            function onCompileProgressChange(progress: string) {
                client.setLoadingState(GameClientLoadingState.LOADING);
                LoadingProgressEventEmitter.fireCompilingProgressEvent(parseFloat(progress));
            }
        )
            .then((unityInstance: Webgl2020GameInstance) => {
                window.gameInstance = unityInstance;
            })
            .catch(WebglClient.handleCreateUnityInstanceError);

        StatWebglLoadingHandler.sendNewUserPrecompileStageFact('instantiateStarted');
        $(window).on('resize', () => {
            WebglClient.stretchClient();
        });
        $(window).trigger('resize');
        WebglClient.stretchClient();
        WebglClient.hide();
        client.setLoadingState(GameClientLoadingState.EMBEDDED);
    }

    private static applyFbGamepadBugFix() {
        // fixes lack of permissions on fb causing client to stop from loading
        navigator.getGamepads = null;
    }

    private static async getShouldShowNewgamePreloader(): Promise<boolean> {
        const newProgress = await UserSession.getInstance().isNewProgress()
            .catch(() => false);
        const adStateAuthFactory = new AdStateAuthFactory();
        let isOrganic: boolean = false;
        try {
            const adState = await adStateAuthFactory.getState();
            isOrganic = adState.getIsOrganic();
        } catch (error) {
            window.Sentry.captureException(error);
            isOrganic = true;
        }
        return newProgress && (isOrganic);
    }

    private static async provideClientDependencies() {
        // Load SDK v4
        window.WebglSendbirdApi = await getWebglSendbirdApi();
    }

    public static getInstantiateConfig() {
        return {
            dataUrl: `${window.app.webglBaseUrl}Build/${this.getBuildJsonFilename()}.data`,
            frameworkUrl: `${window.app.webglBaseUrl}Build/${this.getBuildJsonFilename()}.framework.js`,
            codeUrl: `${window.app.webglBaseUrl}Build/${this.getBuildJsonFilename()}.wasm`,
            streamingAssetsUrl: `${window.app.webglBaseUrl}StreamingAssets`,
            companyName: 'Scorewarrior',
            productName: 'Triumph',
            productVersion: '7205',
            preInit: [
                ()=> { sendNewLoaderFact(NewLoaderFacts.PRE_INIT_STARTED); }
            ],
            preRun: [
                ()=>{ sendNewLoaderFact(NewLoaderFacts.PRE_RUN_STARTED); }
            ],
            postRun: [
                ()=>{ sendNewLoaderFact(NewLoaderFacts.POST_RUN_STARTED); }
            ],
            onRuntimeInitialized: () => {
                sendNewLoaderFact(NewLoaderFacts.RUNTIME_INITIALIZED);
            }
        };
    }

    public static getWebglBuildLink() {
        return `${window.app.webglBaseUrl}Build/${this.getBuildJsonFilename()}.json`;
    }

    public static show() {
        $(UNITY_CONTAINER_SELECTOR).css('left', '0px');
        WebglClient.stretchClient();
    }

    public static hide() {
        $(UNITY_CONTAINER_SELECTOR).css('left', '-999999px');
    }

    public static clientApiReady(): Promise<void> {
        const MAX_TRIES = 100;
        const TRIES_WARNING_COUNT = 20;
        let maxErrorSent = false;
        const COUNTER_START = 0;
        const COUNTER_STEP = 1;
        const COUNTER_TICK_MS = 500;
        const MS_IN_SEC = 1000;

        return new Promise((resolve) => {
            let tryCounter: number = COUNTER_START;
            const tryInterval = setInterval(() => {
                if (tryCounter > TRIES_WARNING_COUNT) {
                    logger.warning(`Waiting for webgl too long, try ${tryCounter}`);
                } else {
                    logger.info(`waiting for webgl api to be ready, try ${tryCounter}`);
                }

                if ((window.createUnityInstance) ||
                    (window.gameInstance && window.gameInstance.SendMessage)) {
                    logger.info(`WebGL Api ready check number ${tryCounter}`);
                    GameClient
                        .getInstance()
                        .sendCommand(new SetKeyboardEnabledCommand({ enabled: 1 }))
                        .then(() => {
                            logger.info(`WebGL Api ready at ${tryCounter} try`);
                            clearInterval(tryInterval);
                            resolve();
                        })
                        .catch(() => {
                            logger.warning('Api not yet ready');
                        });
                }

                if (tryCounter > MAX_TRIES && !maxErrorSent) {
                    const triedForNSeconds = (MAX_TRIES * COUNTER_TICK_MS) / MS_IN_SEC;
                    const errText = '`Game instance init error: ' +
                        `Max await tries exceeded (over ${triedForNSeconds} seconds)`;
                    window.Sentry.captureException(new Error(errText));
                    maxErrorSent = true;
                }

                tryCounter += COUNTER_STEP;
            }, COUNTER_TICK_MS);
        });
    }

    public static clientLoaderReady(): Promise<void> {
        const MAX_TRIES = 100;
        const TRIES_WARNING_COUNT = 20;
        const COUNTER_START = 0;
        const COUNTER_STEP = 1;
        const COUNTER_TICK_MS = 500;
        const MS_IN_SEC = 1000;
        let maxErrorSent = false;

        return new Promise((resolve) => {
            let tryCounter: number = COUNTER_START;
            const tryInterval = setInterval(() => {
                if (tryCounter > TRIES_WARNING_COUNT) {
                    logger.warning(`Waiting for webgl too long, try ${tryCounter}`);
                } else {
                    logger.info(`waiting for unity loader to be ready, try ${tryCounter}`);
                }

                if ((window.createUnityInstance)) {
                    logger.info(`Unity loader ready ready at ${tryCounter} try`);
                    clearInterval(tryInterval);
                    resolve();
                }

                if (tryCounter > MAX_TRIES && !maxErrorSent) {
                    const triedForNSeconds = (MAX_TRIES * COUNTER_TICK_MS) / MS_IN_SEC;
                    const errText = 'Unity loader init error: ' +
                        `Max await tries exceeded (over ${triedForNSeconds} seconds)`;
                    window.Sentry.captureException(new Error(errText));
                    maxErrorSent = true;
                }

                tryCounter += COUNTER_STEP;
            }, COUNTER_TICK_MS);
        });
    }

    public static getBuildJsonFilename(): string {
        return 'Triumph';
    }

    private static showPreloaderBg() {
        PreloaderScreen.getInstance().showPreloader();
        /*
        TODO tmp костыль, сделать нормальный компонент для фулскрин прелоадера
        Сейчас приходится при любых изменениях перерастягивать компонент руками
        */
        onReadyCalcBack();
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static setClientSize(_widthPercent: number, _heightPercent: number) {
        this.widthPercent = _widthPercent;
        this.heightPercent = _heightPercent;
        this.stretchClient();
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public static stretchClient() {
        const gameContainer = $(UNITY_CONTAINER_SELECTOR);
        const gameCanvas = gameContainer.find('canvas');

        let clientHeight = gameContainer.height();
        let clientWidth = gameContainer.width();

        /* eslint-disable @typescript-eslint/no-magic-numbers */
        if ((clientHeight % 2) === 1) {
            clientHeight -= 1;
        }

        if ((clientWidth % 2) === 1) {
            clientWidth -= 1;
        }

        clientWidth = clientWidth * this.widthPercent / 100;
        clientHeight = clientHeight * this.heightPercent / 100;

        if (gameCanvas.width() === clientWidth && gameCanvas.height() === clientHeight) {
            console.info('Game container already has correct size');
            return;
        }

        gameCanvas.css({
            height: `${this.heightPercent}%`,
            left: `${(100 - this.widthPercent) / 2}%`,
            margin: `auto`,
            position: 'relative',
            top: `${(100 - this.heightPercent) / 2}%`,
            width: `${this.widthPercent}%`
        });

        const ratio: number = window.devicePixelRatio;
        const width = (ratio) ? clientWidth * ratio : clientWidth;
        const height = (ratio) ? clientHeight * ratio : clientHeight;

        if (gameCanvas.width() !== width || gameCanvas.height() !== height) {
            gameCanvas[0].width = width;
            gameCanvas[0].height = height;
            const webglContext = gameCanvas[0].getContext('webgl2') || gameCanvas[0].getContext('webgl');
            webglContext.viewport(0, 0, width, height);
        }
    }

    private static setListeners() {
        const gameContainer = $(UNITY_CONTAINER_SELECTOR);
        const gameCanvas = gameContainer.find('canvas');

        $(document).on('paste', (pasteEvent: JQuery.Event) => {
            if ($('.popup').is(':visible')) {
                return;
            }

            catchPaste(pasteEvent, (pasteData: string) => {
                GameClient.getInstance().sendCommand(new PasteClipboardContentCommand({
                    content: pasteData
                }));
            });
        });

        gameCanvas.on('dragstart', (event) => {
            console.log('DRAG START');
            event.preventDefault();
            return false;
        });

        ClientEventEmitter.one(ClientEvents.WEBGL_START_LOADING, () => {
            GtmSender.fireClient(WebglEvents.WEBGL_START_LOADING);
        });

        $(document).on('notEnoughMemory', () => {
            WebglClient.showErrorPopup(
                Localization._n('not_enough_memory'),
                'OK',
                () => { logger.error('Not enough memory'); }
            );
        });

        ClientEventEmitter.one(ClientEvents.WEBGL_FINISH_LOADING, () => {
            GtmSender.fireClient(WebglEvents.WEBGL_FINISH_LOADING);
            showMinimizedSupportChat();
            WebglLoadingAssistant.disable();
        });

        $(window).on('blur', () => {
            window?.gameInstance?.SendMessage &&
            GameClient.getInstance().sendCommand(new SetTabState({active: false}));
        });

        $(window).on('focus', () => {
            window?.gameInstance?.SendMessage &&
            GameClient.getInstance().sendCommand(new SetTabState({active: true}));
        });
    }

    private static getIsWebglSupported(): boolean {
        return window.app.config.user_browser.name.toLowerCase() !== Browsers.IE;
    }

    private static handleCreateUnityInstanceError(message: string) {
        sendCreateUnityInstanceFailedFact(message);

        if (/not support WebGL/i.test(message)) {
            LoadingProgressEventEmitter.fireCompatibilityCheckFailedEvent();
            // popup with message
            WebglClient.showErrorPopup(
                WebglNotSupportedPopup.getNotSupportedMessage(),
                'OK',
                () => { logger.error('WebGL Compatibility check failed'); }
            );
        } else {
            window.Sentry.captureException(new Error(`createUnityInstance failed: ${message}`));

            WebglClient.showErrorPopup(
                'Some error occured during game start, please try again or contact support',
                'OK',
                () => { logger.error(message); }
            );
        }
    }

    private static showErrorPopup(errorMessage: string, buttonText: string, callback: Function) {
        const errorPopup = ErrorSmallPopup.getInstance();
        const popupOptions: { callback: Function, text: string } = {callback, text: buttonText};
        errorPopup.setTitle('WebGL error');
        errorPopup.setHtmlMessage(errorMessage);
        errorPopup.setButtonText(popupOptions.text);
        errorPopup.setCallback(popupOptions.callback);
        errorPopup.show();
    }
}
